import { useEffect, useMemo, useState } from 'react'
import { LoadingOutlined } from '@ant-design/icons'
import { Col, Row, Button, Segmented, Radio, RadioChangeEvent, Modal, Flex, Avatar, Spin } from 'antd'
import { SegmentedValue } from 'antd/es/segmented'
import { format, isAfter, isBefore } from 'date-fns'
import { useNavigate, useParams } from 'react-router-dom'
import styles from './PatientInfo.module.scss'
import { AppConstants } from '../../../../AppContants'
import Loading from '../../../../components/Loading/Loading'
import crossSvg from '../../../../images/cross.svg'
import cross from '../../../../images/crossColored.svg'
import KiroLogo from '../../../../images/kiro-logo-full.svg'
import KiroLogoOrange from '../../../../images/kiro-Logo-Orange.svg'
import KiroLogoRed from '../../../../images/kiro-Logo-Red.svg'
import tick from '../../../../images/tick.svg'

import {
  addAdjustmentCountAsync,
  cancelMembershipAndRemoveBenefitsAsync,
  getPatientByIdAsync,
  getPatientNotesByIdAsync,
  getVisitByPatientIdAsync,
  postCancelAppointmentAsync,
  removeAdjustmentCountAsync,
  removeBenefitsAsync,
  resetGetVisitByPatientId,
  resetEditAdjustmentCount,
  resetuploadFileResponse,
  resetAddAdjustmentCountStatus,
  resetRemoveAdjustmentCountStatus,
  resetMembershipManagementStatus,
} from '../../../../redux/admin/patient'
import { IAppointment, INotes, IPatients, IPatientVisit } from '../../../../redux/admin/patient/interface'
import { useAppDispatch, useAppSelector } from '../../../../redux/store'
import { IQandA, Questions, StatusEnum, VisitStatus } from '../../../../redux/type'
import { calculateDob } from '../../../../utils/Dob'
import { formatPhoneNumber } from '../../../../utils/FormatPhoneNumber'
import { NotificaitonPlacement, notifyError, notifySuccess } from '../../../../utils/Notification'

interface PatientDetailsProps {
  patient: IPatients
  patientVisit?: IPatientVisit
  questionAndAnswer: Record<string, IQandA[]>
  questions: Questions[]
}
interface PatientHealth {
  onClick?: () => void
  title?: any
  options?: any
}

const PatientInfo: React.FC<PatientDetailsProps> = ({ patient, patientVisit, questionAndAnswer, questions }) => {
  const dispatch = useAppDispatch()
  const { id }: any = useParams()
  const navigate = useNavigate()

  const visit = useAppSelector((state) => state.admin.patient.visit)
  const patientByIdStatus = useAppSelector((state) => state.admin.patient.getPatientByIdStatus)
  const notes = useAppSelector((state) => state.admin.patient.notesByPatientId)
  const editAdjustmentCount = useAppSelector((state) => state.admin.patient.editAdjustmentCount)

  const [activeTab, setActiveTab] = useState<SegmentedValue>('Appointments')
  const [radioBtnSelected, setRadioBtnSelected] = useState('upcoming')
  const [selectedNote, setSelectedNote] = useState({
    subjective: '',
    objective: '',
    assessment: '',
    plan: '',
  })
  const [showModal, setShowModal] = useState(false)

  const upcomingAppointments = useMemo(() => {
    const now = new Date()
    return visit?.filter((r: IAppointment) => {
      if (r.status !== VisitStatus.booked) return false
      const appointmentDate = new Date(r?.appointmentTime ?? format(r.appointmentTime || '', 'yyyy-MM-dd'))
      return isAfter(appointmentDate, now)
    })
  }, [visit])

  const missedAppointments = useMemo(() => {
    const now = new Date()
    return visit?.filter((r: IAppointment) => {
      if (r.status !== VisitStatus.booked) return false
      const appointmentDate = new Date(r?.appointmentTime ?? format(r.appointmentTime || '', 'yyyy-MM-dd'))
      return isBefore(appointmentDate, now)
    })
  }, [visit])

  const cancelledAppointments = useMemo(() => {
    return visit?.filter((r: IAppointment) => {
      return r.status === VisitStatus.cancelled
    })
  }, [visit])

  useEffect(() => {
    dispatch(getVisitByPatientIdAsync(id))
    return () => {
      dispatch(resetGetVisitByPatientId(null))
      dispatch(resetuploadFileResponse())
      dispatch(resetEditAdjustmentCount())
    }
  }, [dispatch, id])

  // A paused membership keeps its K badge (orange) even after the paid period
  // has expired, until the pause is resumed and the member is active again.
  const showMembershipK = Boolean(patient?.membership) || patient?.isPaused === true

  const initialLetters = useMemo(() => {
    if (patient === null) {
      return null
    }
    let firstName = patient?.user?.firstname ? patient?.user?.firstname[0]?.toUpperCase() : ''
    let lastName = patient?.user?.lastname ? patient?.user?.lastname[0]?.toUpperCase() : ''
    return firstName + lastName
  }, [patient])

  const filteredQuestions: any = useMemo(() => {
    const filtered: any = questions.filter((item: any) => {
      const isMale = patient?.user?.gender === 'male'
      const isPregnancyIcon = item.iconIdentifier === 'pregnancy'
      let hasNoneOptions = false

      for (const iq of questionAndAnswer[(id && id) ?? ''] ?? []) {
        if (iq.questionId === item.id) {
          for (const o of iq?.responseOptions ?? []) {
            if (o?.option?.text?.toLowerCase() === 'none' || o?.option?.text?.toLowerCase() === 'no') {
              hasNoneOptions = true
              break
            }
          }
          if (hasNoneOptions === true) {
            break
          }
        }
      }
      return !((isMale && isPregnancyIcon) || hasNoneOptions)
    })
    return filtered
  }, [questions, questionAndAnswer, id, patient?.user?.gender])

  useEffect(() => {
    dispatch(getPatientNotesByIdAsync({ patientId: id }))
  }, [dispatch, id])

  useEffect(() => {
    const handleEscKey = (e: any) => {
      if (e.key === 'Escape') {
        navigate(-1)
      }
    }
    document.addEventListener('keydown', handleEscKey)
    return () => {
      document.removeEventListener('keydown', handleEscKey)
    }
  }, [navigate])

  useEffect(() => {
    if (editAdjustmentCount !== null) {
      dispatch(getPatientByIdAsync({ id }))
    }
  }, [editAdjustmentCount, dispatch, id])

  const handleTabChange = (value: SegmentedValue) => {
    setActiveTab(value)
  }

  const handleRadioBtnChange = (e: RadioChangeEvent) => {
    console.log('radio checked', e.target.value)
    setRadioBtnSelected(e.target.value)
  }

  if (patientByIdStatus === StatusEnum.Pending) {
    return (
      <div>
        <Loading style={{ width: '78vw', height: '79vh', background: 'none' }}></Loading>
      </div>
    )
  }

  const stripeDashboardUrl = patient?.stripeCustomerId
    ? `https://dashboard.stripe.com/customers/${patient.stripeCustomerId}`
    : null

  return (
    <div className={styles.patientDetails}>
      {/* <div className={styles.patientDetailsHeading}>Patient Details</div> */}
      <div className={styles.patientProfileContainer}>
        <div className={styles.patientProfile}>
          <div className={styles.patientProfileAndInfo}>
            {!patient?.user?.profilePic && (
              // <div className={styles.nameTag}>
              //   <div className={styles.initialLetters}>{initialLetters}</div>
              // </div>
              <div className={styles.patientProfileImg}>
                <Avatar src={''} shape="square" style={{ borderRadius: 8, minWidth: '126px', minHeight: '126px' }}>
                  <div style={{ fontSize: '5rem' }}>{initialLetters}</div>
                </Avatar>
                {showMembershipK && <MembershipKIcon patient={patient} patientId={id} />}
                {patient?.user?.dob && (
                  <div className={styles.badge} style={{ left: '105px', top: '20px' }}>
                    {calculateDob(patient?.user?.dob)}
                  </div>
                )}
                {patient?.isNew && (
                  <div
                    children={'New'}
                    className={styles.badge}
                    style={{ left: '28px', width: '40px', height: '23px', top: '107px' }}
                  ></div>
                )}
              </div>
            )}
            {patient?.user?.profilePic && (
              <div className={styles.patientProfileImg}>
                <Avatar
                  src={patient?.user?.profilePic}
                  shape="square"
                  style={{
                    borderRadius: 8,
                    minWidth: '126px',
                    minHeight: '126px',
                  }}
                ></Avatar>

                {showMembershipK && <MembershipKIcon patient={patient} patientId={id} />}
                {patient?.user?.dob && (
                  <div className={styles.badge} style={{ left: '105px', top: '20px' }}>
                    {calculateDob(patient?.user?.dob)}
                  </div>
                )}
                {patient?.isNew && (
                  <div
                    children={'New'}
                    className={styles.badge}
                    style={{ left: '28px', width: '40px', height: '23px', top: '107px' }}
                  ></div>
                )}
              </div>
            )}
            <div className={styles.patientInfo}>
              <div
                className={`${styles.patientName} ${patient?.user?.isDeleted === true ? styles.patientNameDeleted : ''}`}
                style={{ minWidth: '150px', textTransform: 'capitalize' }}
              >
                {' ' + (patient?.user?.firstname || '') + ' ' + (patient?.user?.lastname || '')}
              </div>
              <div
                className={styles.patientInfoElement}
                style={{ minWidth: '100px', cursor: 'pointer' }}
                onClick={() => {
                  if (patient?.user?.phone) {
                    window.location.href = `tel:${patient?.user?.phone}`
                  }
                }}
              >
                {patient?.user?.phone ? formatPhoneNumber(patient?.user?.phone) : '-'}
              </div>
              <div
                className={styles.patientInfoElement}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  if (patient?.user?.email || patient?.user?.unverifiedEmail) {
                    window.location.href = patient?.user?.email
                      ? `mailto:${patient?.user?.email}`
                      : `mailto:${patient?.user?.unverifiedEmail}`
                  }
                }}
              >
                {patient?.user?.email || patient?.user?.unverifiedEmail
                  ? patient?.user?.email
                    ? patient?.user?.email
                    : patient?.user?.unverifiedEmail
                  : '-'}
              </div>
              {stripeDashboardUrl ? (
                <div className={styles.patientInfoElement}>
                  <a href={stripeDashboardUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#007f7c' }}>
                    Stripe
                  </a>
                </div>
              ) : null}
              <div className={styles.patientInfoElement} style={{ textTransform: 'capitalize' }}>
                {patient?.source ? patient?.source : '-'}{' '}
              </div>
              {/* <div className={styles.patientNameAgeGender}>{' ' + patientAge} </div> */}
              {/* <div
              className={styles.patientAgeGender}
            >{`${patientAge}${patient?.user?.gender !== null ? (patient?.user?.gender?.toLowerCase() === 'others' ? ', Other' : ', ' + patient?.user?.gender) : ''}`}</div> */}
            </div>
          </div>
          <div className={styles.metricWrapper}>
            <div className={styles.rowOne}>
              {patient?.feedbackValue === null ? (
                <div className={styles.flashingMetricCard}>
                  <div className={styles.metricTitle}>REV</div>
                  <div className={styles.metricValue}>{patient?.feedbackValue ?? '-'}</div>
                </div>
              ) : (
                <div className={styles.metricCard}>
                  <div className={styles.metricTitle}>REV</div>
                  <div className={styles.metricValue}>{patient?.feedbackValue ?? '-'}</div>
                </div>
              )}

              <div className={styles.metricCard}>
                <div className={styles.metricTitle}>ATD</div>
                <div className={styles.metricValue}>{patient?.metric?.atd ?? '-'}</div>
              </div>

              {patient && patient?.metric && patient?.metric?.dst > 7 ? (
                <div className={styles.flashingMetricCard}>
                  <div className={styles.metricTitle}>DSL</div>
                  <div className={styles.metricValue}>{patient?.metric?.dst ?? '-'}</div>
                </div>
              ) : (
                <div className={styles.metricCard}>
                  <div className={styles.metricTitle}>DSL</div>
                  <div className={styles.metricValue}>{patient?.metric?.dst ?? '-'}</div>
                </div>
              )}
              {patient && patient?.metric && patient?.metric?.apm && parseFloat(patient?.metric?.apm) < 4 ? (
                <div className={styles.flashingMetricCard}>
                  <div className={styles.metricTitle}>APM</div>
                  <div className={styles.metricValue}>{patient?.metric?.apm ?? '-'}</div>
                </div>
              ) : (
                <div className={styles.metricCard}>
                  <div className={styles.metricTitle}>APM</div>
                  <div className={styles.metricValue}>{patient?.metric?.apm ?? '-'}</div>
                </div>
              )}
            </div>
            <div className={styles.rowTwo}>
              {patient?.metric?.membershipWeek === 4 && (
                <div className={styles.flashingMetricCard}>
                  <div className={styles.metricTitle}>ZON</div>
                  <div className={styles.metricValue}>
                    {patient?.metric?.membershipMonth ?? '-'}.{patient?.metric?.membershipWeek ?? '-'}
                  </div>
                </div>
              )}
              {patient?.metric?.membershipWeek !== 4 && (
                <div
                  className={styles.metricCard}
                  style={{
                    backgroundColor: patient?.metric?.membershipWeek === 3 ? '#ff0000' : '#20212e',
                    color: patient?.metric?.membershipWeek === 3 ? '#e5e7eb' : '#007f7c',
                  }}
                >
                  <div className={styles.metricTitle}>ZON</div>
                  {patient?.metric?.membershipMonth === 0 && patient?.metric?.membershipWeek === 0 ? (
                    <div className={styles.metricValue}>{'-'}</div>
                  ) : (
                    <div className={styles.metricValue}>
                      {patient?.metric?.membershipMonth ?? '-'}.{patient?.metric?.membershipWeek ?? '-'}
                    </div>
                  )}
                </div>
              )}
              <div className={styles.metricCard}>
                <div className={styles.metricTitle}>PCR</div>
                <div className={styles.metricValue}>{patient?.subscriptionCount ?? 0}</div>
              </div>
              <div className={styles.metricCard}>
                <div className={styles.metricTitle}>NSS</div>
                <div className={styles.metricValue}>{patient?.metric?.scanScore ?? '-'}</div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.actionButtonsContainer}>
          <div className={styles.actionsContainer}>
            {/* {patient?.screeningStatus === 'not_booked' && (
              <Buttons
                children={'Screening not booked'}
                style={{
                  backgroundColor: 'transparent',
                  padding: 5,
                  color: '#007f7c',
                  border: '1px solid #007f7c',
                }}
              ></Buttons>
            )} */}
            {/* {patient?.screeningStatus === 'rejected' && (
              <Buttons
                children={'Rejected'}
                style={{
                  backgroundColor: 'transparent',
                  color: '#ff4141',
                  border: '1px solid #ff4141',
                }}
              ></Buttons>
            )} */}
            {/* {patient?.screeningStatus === 'accepted' && (
              <Buttons
                children={'Approved'}
                style={{
                  backgroundColor: 'transparent',
                  color: '#007f7c',
                  border: '1px solid #007f7c',
                }}
              ></Buttons>
            )} */}
            {/* {patient?.screeningStatus === 'screening_booked' && (
              <div className={styles.actionsContainer}>
                <Buttons
                  onClick={() => {
                    window.open(visit?.zoomLink, '_blank')
                  }}
                  style={{
                    opacity: visitStatus === StatusEnum.Pending ? '0.7' : '',
                    backgroundColor: '#0095D5',
                    backgroundImage: visitStatus === StatusEnum.Pending ? '' : `url(${zoom})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: '15%  center',
                    paddingLeft: '2rem',
                    width: '179px',
                    color: '#fffeff',
                    height: '38px',
                    borderRadius: '0.4rem',
                    fontWeight: 700,
                  }}
                >
                  {visitStatus === StatusEnum.Pending ? (
                    <Spin
                      size="small"
                      indicator={<LoadingOutlined style={{ color: '#fff', right: '0.8rem' }} spin />}
                    />
                  ) : (
                    appointmentDateTime?.date + ', ' + appointmentDateTime?.time
                  )}
                </Buttons>

                <Buttons
                  onClick={() => {
                    handleReject()
                  }}
                  children={'Reject'}
                  style={{
                    backgroundColor: '#D50000',
                  }}
                ></Buttons>
                <Buttons
                  onClick={handleApprove}
                  children={'Approve'}
                  style={{ width: '179px', height: '38px', fontWeight: 700 }}
                ></Buttons>
              </div>
            )} */}
          </div>
          {/* <div>
            {patient?.screeningStatus === 'screening_booked' && (
              <div className={styles.dateIdentifier}>
                {visitStatus === StatusEnum.Pending && (
                  <Spin size="small" indicator={<LoadingOutlined style={{ color: '#fff', right: '0.8rem' }} spin />} />
                )}
                {visitStatus === StatusEnum.Success && (
                  <div>
                    <span>{appointmentDateTime?.date + ','}</span>
                    <span style={{ marginLeft: '5px' }}> {appointmentDateTime?.time}</span>
                  </div>
                )}
              </div>
            )}
          </div> */}
          {
            <div className={styles.creditWrapper}>
              <EditCredits patient={patient}></EditCredits>
              {/* <div className={styles.creditContainer}>
                {patientByIdStatus === StatusEnum.Success && (
                  <div className={styles.creditIdentifier}>
                    <span>
                      Available Credits:&nbsp;
                      {adjustmentCount?.remainingVisitCount ?? patient?.existingCount ?? '-'}
                    </span>
                  </div>
                )}
              </div> */}
            </div>
          }
        </div>
      </div>

      <div className={styles.patientDetailsContent}>
        {filteredQuestions.length !== 0 && <div className={styles.pastHealthConditionTitle}>Health Conditions</div>}
        <div className={styles.healthConditionsContainer}>
          <Row gutter={[10, 8]} align={'stretch'}>
            {filteredQuestions.map((item: any, index: number) => {
              let responseOptions: any = []
              let title: any
              for (const q in AppConstants.Questions) {
                if (item.iconIdentifier === q) {
                  title = AppConstants.Questions[q]
                  break
                }
              }
              questionAndAnswer[patient?.id]?.forEach((iq) => {
                if (iq.questionId === item.id) {
                  responseOptions = iq.responseOptions
                }
              })
              return (
                <Col xs={{ span: 17 }} sm={{ span: 17 }} md={{ span: 9 }} lg={{ span: 8 }} xl={{ span: 8 }} key={index}>
                  <div key={index}>
                    <PatientHealthCondition title={title} options={responseOptions}></PatientHealthCondition>
                  </div>
                </Col>
              )
            })}
          </Row>
        </div>
        <div className={styles.toggleAppointments}>
          <Segmented options={['Appointments', 'Notes']} value={activeTab} onChange={handleTabChange}></Segmented>
        </div>
        {activeTab === 'Appointments' && (
          <div className={styles.radioBtnContainer}>
            <Radio.Group onChange={handleRadioBtnChange} value={radioBtnSelected}>
              <Radio value={'upcoming'} defaultChecked>
                Upcoming
              </Radio>
              <Radio value={'missed'}>Missed</Radio>
              <Radio value={'cancelled'}>Cancelled</Radio>
            </Radio.Group>
          </div>
        )}
        {activeTab === 'Appointments' && (
          <div className={styles.listContainer}>
            {radioBtnSelected === 'upcoming' && (
              <div className={styles.cardList}>
                {upcomingAppointments?.map((a: any) => (
                  <AppointmentCardItem
                    key={a.id}
                    hideCancelled={false}
                    appointments={a}
                    onCancel={() => {
                      dispatch(postCancelAppointmentAsync({ id: a.id }))
                    }}
                  />
                ))}
                {upcomingAppointments?.length === 0 && (
                  <div className={styles.noAppointments}>No upcoming appointments</div>
                )}
              </div>
            )}
            {radioBtnSelected === 'missed' && (
              <div className={styles.cardList}>
                {missedAppointments?.map((a: any) => (
                  <AppointmentCardItem
                    key={a.id}
                    appointments={a}
                    hideCancelled={true}
                    onCancel={() => {
                      dispatch(postCancelAppointmentAsync({ id: a.id }))
                    }}
                  />
                ))}
                {missedAppointments?.length === 0 && (
                  <div className={styles.noAppointments}>No missed appointments</div>
                )}
              </div>
            )}
            {radioBtnSelected === 'cancelled' && (
              <div className={styles.cardList}>
                {cancelledAppointments?.map((a: any) => (
                  <AppointmentCardItem key={a.id} appointments={a} hideCancelled={true} />
                ))}
                {cancelledAppointments?.length === 0 && (
                  <div className={styles.noAppointments}>No cancelled appointments</div>
                )}
              </div>
            )}
          </div>
        )}
        {activeTab === 'Notes' && (
          <div className={styles.listContainer}>
            <div className={styles.cardList}>
              {notes?.map((a: INotes) => (
                <NotesCard
                  key={a.id}
                  note={a}
                  onClick={() => {
                    setSelectedNote({
                      ...selectedNote,
                      subjective: a?.subjective ?? '',
                      objective: a?.objective ?? '',
                      assessment: a?.assessment ?? '',
                      plan: a?.plan ?? '',
                    })
                    setShowModal(true)
                  }}
                />
              ))}
              {notes?.length === 0 && <div className={styles.noAppointments}>No notes available</div>}
            </div>
          </div>
        )}
        {showModal && activeTab === 'Notes' && (
          <NotesModal
            closeModal={() => {
              setShowModal(false)
            }}
            showModal={showModal}
            note={selectedNote}
          ></NotesModal>
        )}

        {/* <div className={styles.feedbackTitle}>Feedback</div> */}
        {/* <TextArea
          className={styles.feedbackArea}
          autoSize={{ minRows: 6 }}
          onChange={(e) => {
            setReason(e.target.value)
          }}
          placeholder={'Insert comments here...'}
          value={reason}
          disabled={
            patient?.screeningStatus === 'not_booked' ||
            patient?.screeningStatus === 'rejected' ||
            patient?.screeningStatus === 'accepted'
              ? true
              : false
          }
        /> */}
        {/* <div className={styles.feedbackUploadContainer}>
          <div className={styles.feedbackContainer}>
            <div
              style={{
                display: 'flex',
                flex: 1,
                paddingTop: '3rem',
                paddingBottom: '3rem',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <div className={styles.uploadBar}>
                <Upload.Dragger
                  {...props}
                  onChange={handleFileChange}
                  showUploadList={false}
                  maxCount={1}
                  style={{ border: 'none', backgroundColor: '#20212e' }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div className={styles.cloudUploadSign}>
                      <img src={cloudUpload} alt={'cloud upload'} className={styles.icon}></img>
                    </div>
                    <p className={styles.dragText}> Drag your file here</p>
                    <div className={styles.textContent}>OR</div>
                    <Button
                      children="Upload File"
                      style={{
                        width: '12.1rem',
                        backgroundImage: `url(${uploadButton})`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: '15%  center',
                        paddingLeft: '5rem',
                        paddingRight: '3rem',
                      }}
                    ></Button>
                  </div>
                </Upload.Dragger>
              </div>
            </div>
            {loading ? (
              <div className={styles.loadingContainer}>
                <Spin />
              </div>
            ) : fileList !== '' && fileList !== null ? (
              <div className={styles.uploadedFilesContainer}>
                <div className={styles.uploadedFiles}>
                  <div className={styles.uploadText}>Uploaded files</div>
                  <div className={styles.fileListBar}>
                    <img style={{ marginRight: '0.8rem' }} src={FileSvg} alt="FileSvg" />
                    <p
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        width: '100%',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {fileList}
                    </p>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div> */}
      </div>
    </div>
  )
}
export default PatientInfo

export function PatientHealthCondition({ title, options, onClick }: PatientHealth) {
  return (
    <div className={styles.questionnareParentContainer}>
      <div className={styles.questionnareContainer} onClick={onClick}>
        <div className={styles.questionnareTitle}>{title}</div>
        <div className={styles.optionsContainer}>
          {options && options.length > 0 ? (
            options.map((item: any, index: number) => (
              <div key={index} className={styles.optionItemsContainer}>
                <div className={styles.optionTick}>
                  <img src={tick} alt="tick sign" />
                </div>
                <div className={styles.optionItem}>{item?.option?.text}</div>
              </div>
            ))
          ) : (
            <div className={styles.optionItemsContainer}>
              <div className={styles.optionTick}>
                <img src={cross} alt="cross sign" />
              </div>
              <div className={styles.optionItem}>No answer yet</div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

interface AppointmentCardItemProps {
  appointments?: IAppointment
  hideCancelled?: boolean
  isCancelled?: boolean
  onClick?: () => void
  onCancel?: () => void
}

export const AppointmentCardItem = ({
  appointments,
  onClick,
  isCancelled,
  onCancel,
  hideCancelled,
}: AppointmentCardItemProps) => {
  let appointmentDetails: any = useMemo(() => {
    if (appointments?.appointmentTime) {
      return {
        time: appointments?.appointmentTime ? format(appointments.appointmentTime, 'h:mm a') : '',
        date: appointments?.appointmentTime ? format(appointments.appointmentTime, 'MM-dd-yyyy') : '',
        location: appointments?.clinic?.name || '-',
      }
    } else {
      return {
        time: '-',
        date: '-',
        location: '-',
      }
    }
  }, [appointments])

  return (
    <div className={styles.container} onClick={onClick}>
      <div className={styles.wrapper}>
        <div className={styles.columnOne}>{appointmentDetails?.date || ''}</div>
        <div className={styles.columnTwo}>{appointmentDetails?.time}</div>
        <div className={styles.columnThree}>{appointmentDetails?.location}</div>
      </div>
      {!hideCancelled && (
        <div className={styles.actionColumn}>
          <Button className={styles.cancelBtn} onClick={onCancel}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  )
}

interface NotesCardProps {
  note?: INotes
  onClick?: () => void
}
export const NotesCard = ({ note, onClick }: NotesCardProps) => {
  let noteDetails: any = useMemo(() => {
    if (note?.createdAt) {
      return {
        date: note?.visit.appointmentTime ? format(note?.createdAt, 'MM-dd-yyyy') : '',
        time: note?.visit.appointmentTime ? format(note?.visit.appointmentTime, 'h:mm a') : '',
      }
    } else {
      return null
    }
  }, [note])

  return (
    <div className={styles.container} onClick={onClick}>
      <div className={styles.wrapper}>
        <div className={styles.columnOne}>
          {note && <span>{' ' + note?.chiropractor?.user?.firstname + ' ' + note?.chiropractor?.user?.lastname}</span>}
        </div>
        <div className={styles.columnTwo}>{noteDetails?.date}</div>
        <div className={styles.columnThree}>{noteDetails?.time}</div>
      </div>

      <div className={styles.columnThree}>
        <Button
          className={styles.statusTag}
          children={note?.status && note?.status === 'accepted' ? 'Approved' : 'Pending'}
          onClick={() => {}}
          style={{
            color: note?.status === 'pending' ? '#8c8678' : '#007f7c',
            border: note?.status === 'pending' ? '1px solid #8c8678' : '1px solid #007f7c',
          }}
        ></Button>
      </div>
    </div>
  )
}

type MembershipActionType = 'remove-benefits' | 'cancel-membership'

interface MembershipKIconProps {
  patient: IPatients
  patientId: string
}

const MembershipKIcon = ({ patient, patientId }: MembershipKIconProps) => {
  const dispatch = useAppDispatch()
  const membershipManagementStatus = useAppSelector((state) => state.admin.patient.membershipManagementStatus)
  const membershipManagementError = useAppSelector((state) => state.admin.patient.membershipManagementError)
  const isUnsubscribed = patient?.membershipStatus === 'in-active'
  // A paused member stays membershipStatus 'active', so the pause is only
  // visible through this flag — without it they are indistinguishable from an
  // ordinary member on this screen. The pause outlives the paid period: the K
  // stays orange even after membership expiry, until the pause is resumed.
  const isPaused = patient?.isPaused === true
  const [showModal, setShowModal] = useState(false)
  const [pendingAction, setPendingAction] = useState<MembershipActionType | null>(null)

  const modalTitle = isUnsubscribed ? 'Remove benefits?' : 'Cancel membership and remove benefits?'
  const isLoading = membershipManagementStatus === StatusEnum.Pending

  const openModal = () => {
    if (isLoading) {
      return
    }
    setPendingAction(isUnsubscribed ? 'remove-benefits' : 'cancel-membership')
    setShowModal(true)
  }

  const closeModal = () => {
    if (isLoading) {
      return
    }
    setShowModal(false)
    setPendingAction(null)
  }

  const handleConfirm = () => {
    if (!patientId || isLoading) {
      return
    }
    if (pendingAction === 'remove-benefits') {
      dispatch(removeBenefitsAsync({ userId: patientId }))
    } else {
      dispatch(cancelMembershipAndRemoveBenefitsAsync({ userId: patientId }))
    }
  }

  useEffect(() => {
    if (membershipManagementStatus === StatusEnum.Success) {
      notifySuccess(pendingAction === 'remove-benefits' ? 'Benefits removed' : 'Membership cancelled', '')
      setShowModal(false)
      setPendingAction(null)
      dispatch(getPatientByIdAsync({ id: patientId }))
      dispatch(resetMembershipManagementStatus())
    } else if (membershipManagementStatus === StatusEnum.Failed) {
      notifyError(
        pendingAction === 'remove-benefits' ? 'Unable to remove benefits' : 'Unable to cancel membership',
        membershipManagementError || '',
        NotificaitonPlacement.bottomRight,
      )
      dispatch(resetMembershipManagementStatus())
    }
  }, [membershipManagementStatus, membershipManagementError, pendingAction, patientId, dispatch])

  useEffect(() => {
    return () => {
      dispatch(resetMembershipManagementStatus())
    }
  }, [dispatch])

  return (
    <>
      <img
        className={`${styles.badge} ${styles.membershipBadge}`}
        src={isPaused ? KiroLogoOrange : isUnsubscribed ? KiroLogoRed : KiroLogo}
        alt={isPaused ? 'membership paused' : isUnsubscribed ? 'membership cancelled' : 'membership active'}
        title={isPaused ? 'Membership paused' : undefined}
        role="button"
        tabIndex={0}
        onClick={openModal}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            openModal()
          }
        }}
        style={{
          marginLeft: '8px',
          background: 'none',
          cursor: isLoading ? 'wait' : 'pointer',
          opacity: isLoading ? 0.6 : 1,
        }}
      />
      <MembershipConfirmModal
        showModal={showModal}
        title={modalTitle}
        isLoading={isLoading}
        onConfirm={handleConfirm}
        closeModal={closeModal}
      />
    </>
  )
}

interface MembershipConfirmModalProps {
  showModal: boolean
  title: string
  isLoading: boolean
  onConfirm: () => void
  closeModal: () => void
}

const MembershipConfirmModal = ({
  showModal,
  title,
  isLoading,
  onConfirm,
  closeModal,
}: MembershipConfirmModalProps) => {
  return (
    <Modal
      open={showModal}
      centered
      closable={false}
      mask={true}
      maskClosable={!isLoading}
      footer={null}
      onCancel={closeModal}
    >
      <div className={styles.membershipModal}>
        <div className={styles.membershipModalTitle}>{title}</div>
        <Flex gap={12} justify="center" className={styles.membershipModalActions}>
          <Button className={styles.membershipModalCancel} disabled={isLoading} onClick={closeModal}>
            No
          </Button>
          <Button className={styles.membershipModalConfirm} disabled={isLoading} onClick={onConfirm}>
            {isLoading ? <Spin indicator={<LoadingOutlined style={{ color: '#fffeff' }} spin />} /> : 'Yes'}
          </Button>
        </Flex>
      </div>
    </Modal>
  )
}

interface NotesModalProps {
  note?: Note
  showModal: boolean
  closeModal: () => void
  onClick?: () => void
}

interface Note {
  subjective: string
  objective: string
  assessment: string
  plan: string
}

export const NotesModal = ({ note, showModal, closeModal }: NotesModalProps) => {
  return (
    <>
      <Modal
        open={showModal}
        centered
        closable={true}
        mask={true}
        maskClosable={true}
        footer={null}
        onCancel={closeModal}
        closeIcon={
          <div
            style={{
              width: '28px',
              height: '26px',
              position: 'absolute',
              top: -40,
              right: -45,
              background: '#191A23',
              borderRadius: '4px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            onClick={() => {
              closeModal()
            }}
          >
            <img src={crossSvg} alt="close icon"></img>
          </div>
        }
      >
        <Flex vertical gap={8}>
          <div className={styles.cardWrapper}>
            <div className={styles.textHeader}>Subjective</div>
            <div className={styles.textContainer}>{note?.subjective}</div>
          </div>

          <div className={styles.cardWrapper}>
            <div className={styles.textHeader}>Objective</div>
            <div className={styles.textContainer}>{note?.objective}</div>
          </div>
          <div className={styles.cardWrapper}>
            <div className={styles.textHeader}>Assessment</div>
            <div className={styles.textContainer}>{note?.assessment}</div>
          </div>
          <div className={styles.cardWrapper}>
            <div className={styles.textHeader}>Plan</div>
            <div className={styles.textContainer}>{note?.plan}</div>
          </div>
        </Flex>
      </Modal>
    </>
  )
}
interface Credits {
  patient: IPatients
}
export const EditCredits = ({ patient }: Credits) => {
  const { id } = useParams()
  const dispatch = useAppDispatch()

  const apiError = useAppSelector((state) => state.admin.patient.editAdjustmentError)
  const addAdjustmentCountStatus = useAppSelector((state) => state.admin.patient.addAdjustmentCountStatus)
  const removeAdjustmentCountStatus = useAppSelector((state) => state.admin.patient.removeAdjustmentCountStatus)
  const applyCredit = (planType: string, operationType: string) => {
    if (!id) {
      return
    }
    if (operationType === 'add-count') {
      dispatch(
        addAdjustmentCountAsync({
          patientId: id,
          count: '1',
          operationType: operationType,
          planType: planType,
        }),
      )
    } else if (operationType === 'decrease-count') {
      dispatch(
        removeAdjustmentCountAsync({
          patientId: id,
          count: '1',
          operationType: operationType,
          planType: planType,
        }),
      )
    }
  }

  useEffect(() => {
    if (removeAdjustmentCountStatus === StatusEnum.Failed) {
      notifyError('Unable to remove credit', apiError, NotificaitonPlacement.bottomRight)
    }
    if (addAdjustmentCountStatus === StatusEnum.Failed) {
      notifyError('Unable to add credit', NotificaitonPlacement.bottomRight)
    }
    return () => {
      dispatch(resetAddAdjustmentCountStatus())
      dispatch(resetRemoveAdjustmentCountStatus())
    }
  }, [removeAdjustmentCountStatus, addAdjustmentCountStatus, apiError, dispatch])

  return (
    <div className={styles.creditsComponent}>
      <div className={styles.credits}>
        <div className={styles.type} style={{ color: !patient?.membership ? '#4f505e' : '#d2d3eo' }}>
          Plan
        </div>
        <div className={styles.operatorWrapper} style={{ marginLeft: 12 }}>
          <div
            className={styles.value}
            style={{ opacity: !patient?.membership ? 0.8 : 1, color: !patient?.membership ? '#4f505e' : '#d2d3eo' }}
          >
            {patient?.subscriptionCount ?? '-'}
          </div>
          <div
            className={styles.operator}
            style={{
              opacity: !patient?.membership || patient?.subscriptionCount === 0 ? 0.3 : 1,
              cursor: !patient?.membership || patient?.subscriptionCount === 0 ? 'not-allowed' : 'pointer',
            }}
            onClick={() =>
              !patient?.membership || patient?.subscriptionCount === 0
                ? undefined
                : applyCredit('subscription', 'decrease-count')
            }
          >
            <span style={{ marginTop: -2 }}>-</span>
          </div>
          <div
            className={styles.operator}
            style={{
              opacity: !patient?.membership ? 0.3 : 1,
              cursor: !patient?.membership ? 'not-allowed' : 'pointer',
            }}
            onClick={() => (patient?.membership ? applyCredit('subscription', 'add-count') : undefined)}
          >
            <span>+</span>
          </div>
        </div>
      </div>
      <div className={styles.credits} style={{ marginTop: 8 }}>
        <div className={styles.type}>Other</div>
        <div className={styles.operatorWrapper}>
          <div className={styles.value}>{patient?.onDemandCount ?? '-'}</div>
          <div
            className={styles.operator}
            style={{
              opacity: patient?.onDemandCount === 0 ? 0.3 : 1,
              cursor: patient?.onDemandCount === 0 ? 'not-allowed' : 'pointer',
            }}
            onClick={() => (patient.onDemandCount === 0 ? undefined : applyCredit('on-demand', 'decrease-count'))}
          >
            <span style={{ marginTop: -2 }}>-</span>
          </div>
          <div className={styles.operator} onClick={() => applyCredit('on-demand', 'add-count')}>
            <span>+</span>
          </div>
        </div>
      </div>
    </div>
  )
}
